/**
 * WHAT A REFUSAL FROM `authors-api` MEANS. Pure, synchronous, and the most important 120 lines in
 * this feature.
 *
 * THE ONE RULE THIS FILE EXISTS TO ENFORCE. adr/0010-firebase-integration.md rules that the
 * guard answers "503 for `cannot verify right now` and 401 only for `this token is invalid`, so
 * the apps retry instead of signing people out". The 503 response component says the same thing in
 * its own words: "The two are deliberately different answers so that a Google outage does not sign
 * every author out of the cabinet" (docs/design/07-api/authors-api.yaml, generated into
 * `components.responses.Problem503` with an optional integer `Retry-After` header).
 *
 * A 503 THAT SIGNS THE AUTHOR OUT IS THE DEFECT. It is easy to write by accident — one
 * `if (!response.ok) signOut()` does it — and impossible to notice until Google has an outage and
 * ~530 authors are logged out at once. So the decision is a pure function with an exhaustive test
 * over every status code rather than a branch buried in a component.
 *
 * WHY THIS BRANCHES ON THE HTTP STATUS AND NOT ON `error_code`, WHICH THE CORPUS NORMALLY
 * REQUIRES. `Problem.error_code` is "the machine code a client branches on. This is the contract;
 * `title` and `detail` are not" (docs/design/07-api/shared.yaml) — and the 46-code `ErrorCode`
 * enum it was written against contains NO code meaning "this token is invalid" and NO code meaning
 * "cannot verify right now". That is a real contract gap, and it has CLOSED: `UNAUTHENTICATED` and
 * `UPSTREAM_UNAVAILABLE` are members now (65; decision-log row 159). The status line carries the
 * same split adr/0010 rules and stays what this reads; the codes agree with it, not decide it.
 */
import type {
  SchemaErrorCode,
  SchemaProblem,
} from '../../../../../../shared/types/generated/authors-api';
import { headerValue } from './http';
import type { HttpHeaders, HttpResponse } from './http';

export type ErrorCode = SchemaErrorCode;
export type Problem = SchemaProblem;

/**
 * How long to wait when the platform refuses without telling us how long to wait.
 *
 * `Problem429` on this surface declares a required integer `Retry-After` since decision-log row
 * 220 (row 170: every 429 sends one), so a 429 is read like a 503 below; this default is for an
 * answer that arrives without a readable one anyway (a proxy's), chosen once here, not per screen.
 */
export const DEFAULT_BACKOFF_SECONDS = 30;

/** The ceiling on anything a server asks us to wait. A hostile or buggy header is not a hang. */
export const MAX_BACKOFF_SECONDS = 300;

/**
 * What went wrong, in the vocabulary the cabinet actually renders.
 *
 * These are not HTTP statuses renamed. `identity-unavailable` and `unauthenticated` are the two
 * halves of the ruled split, and they are separate members precisely so that no screen can treat
 * them as one.
 */
export type ApiFailure =
  | {
      /** 401. The credential is missing, invalid or revoked. This one, and only this one, signs out. */
      readonly kind: 'unauthenticated';
      /**
       * True when `WWW-Authenticate` carried `error="invalid_token"` — the bearer surfaces send
       * that "only when a token was presented and failed ... the one that stops a client retrying
       * a token that will never work" (docs/design/07-api/shared.yaml). A bare
       * `Bearer realm="moonx"` instead means no credential was presented, which can be our own
       * bug, and is worth exactly one forced token refresh before giving up.
       */
      readonly tokenPresentedAndFailed: boolean;
      readonly errorCode: ErrorCode | null;
      readonly problem: Problem | null;
    }
  | {
      /** 503. Google was unreachable. HOLD THE SESSION. */
      readonly kind: 'identity-unavailable';
      readonly retryAfterSeconds: number;
      readonly problem: Problem | null;
    }
  | {
      /** 429. Also holds the session; the back-off is the `Retry-After` it declares (row 220). */
      readonly kind: 'rate-limited';
      readonly retryAfterSeconds: number;
      readonly problem: Problem | null;
    }
  | {
      /** 400, 403, 404, 409, 422 — the platform understood us and said no. Never a sign-out. */
      readonly kind: 'refused';
      readonly status: number;
      readonly errorCode: ErrorCode | null;
      readonly problem: Problem | null;
    }
  | {
      /** No HTTP answer at all. Retry; never sign out. */
      readonly kind: 'unreachable';
    }
  | {
      /** An answer the operation does not declare. Retried, never a sign-out. */
      readonly kind: 'unexpected';
      readonly status: number;
      readonly retryAfterSeconds: number;
      readonly problem: Problem | null;
    };

/** What the app should DO about a failure. One value per member, so nothing is implicit. */
export type FailureAction =
  'sign-out' | 'refresh-token-once' | 'hold-session-and-retry' | 'show-refusal';

/**
 * THE INVARIANT, in one function.
 *
 * Only `unauthenticated` ever ends a session. `identity-unavailable` — every 503, with or without
 * a `Retry-After`, with or without a problem body — holds it and retries. The test over the whole
 * 100..599 range asserts exactly that.
 */
export function actionFor(failure: ApiFailure): FailureAction {
  switch (failure.kind) {
    case 'unauthenticated':
      return failure.tokenPresentedAndFailed ? 'sign-out' : 'refresh-token-once';
    case 'identity-unavailable':
    case 'rate-limited':
    case 'unreachable':
    case 'unexpected':
      return 'hold-session-and-retry';
    case 'refused':
      return 'show-refusal';
  }
}

/** True only where the corpus permits ending the session. Read it rather than re-deriving it. */
export function endsSession(failure: ApiFailure): boolean {
  return actionFor(failure) === 'sign-out';
}

/**
 * `Retry-After`, as this surface declares it: an INTEGER NUMBER OF SECONDS, minimum 0
 * (docs/design/07-api/authors-api.yaml; generated as `"Retry-After"?: number`).
 *
 * RFC 9110 also allows an HTTP-date form and this deliberately does not accept one: the contract
 * declares an integer, and silently accepting a second syntax is how a client and a server come to
 * disagree about what was sent. A value we cannot read falls back to the default rather than to
 * zero, because zero is a retry storm.
 */
export function parseRetryAfterSeconds(headers: HttpHeaders): number {
  const raw = headerValue(headers, 'retry-after');
  if (raw === null) {
    return DEFAULT_BACKOFF_SECONDS;
  }

  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) {
    return DEFAULT_BACKOFF_SECONDS;
  }

  const seconds = Number.parseInt(trimmed, 10);
  if (!Number.isFinite(seconds)) {
    return DEFAULT_BACKOFF_SECONDS;
  }

  return Math.min(Math.max(seconds, 0), MAX_BACKOFF_SECONDS);
}

/**
 * `WWW-Authenticate: Bearer realm="moonx", error="invalid_token"` — the second half of the ruled
 * 401 split. Matched on the parameter rather than on the whole string so that a differently
 * ordered or differently spaced challenge still reads correctly.
 */
export function tokenWasRejected(headers: HttpHeaders): boolean {
  const challenge = headerValue(headers, 'www-authenticate');
  if (challenge === null) {
    return false;
  }
  return /(^|[\s,])error\s*=\s*"?invalid_token"?/i.test(challenge);
}

/**
 * Reads a problem document out of an unparsed body.
 *
 * Defensive on purpose: an edge can answer with an HTML error page, and the legacy editor's
 * audited defect was exactly that — a long article silently failed to save and the client was
 * handed an HTML body it could not parse. A body that is not a problem document is `null` here,
 * never a half-filled object.
 */
export function readProblem(body: unknown): Problem | null {
  if (typeof body !== 'object' || body === null) {
    return null;
  }

  const candidate = body as Partial<Record<keyof Problem, unknown>>;
  if (typeof candidate.error_code !== 'string') {
    return null;
  }
  if (typeof candidate.title !== 'string' || typeof candidate.status !== 'number') {
    return null;
  }

  return body as Problem;
}

/** The `error_code` alone, when the body carried one. `null` is a real answer, not an omission. */
export function readErrorCode(body: unknown): ErrorCode | null {
  return readProblem(body)?.error_code ?? null;
}

/**
 * The whole classification. `response` must be a non-2xx; a 2xx here is a programming error and
 * is reported as one rather than silently classified.
 */
export function classifyFailure(response: HttpResponse): ApiFailure {
  const { status, headers, body } = response;
  const problem = readProblem(body);

  if (status >= 200 && status < 300) {
    throw new Error(`classifyFailure was given a success status (${String(status)}).`);
  }

  if (status === 401) {
    return {
      kind: 'unauthenticated',
      tokenPresentedAndFailed: tokenWasRejected(headers),
      errorCode: problem?.error_code ?? null,
      problem,
    };
  }

  if (status === 503) {
    return {
      kind: 'identity-unavailable',
      retryAfterSeconds: parseRetryAfterSeconds(headers),
      problem,
    };
  }

  if (status === 429) {
    return {
      kind: 'rate-limited',
      retryAfterSeconds: parseRetryAfterSeconds(headers),
      problem,
    };
  }

  if (status >= 400 && status < 500) {
    return { kind: 'refused', status, errorCode: problem?.error_code ?? null, problem };
  }

  return {
    kind: 'unexpected',
    status,
    retryAfterSeconds: parseRetryAfterSeconds(headers),
    problem,
  };
}
